import './outcome-row.css';
import '@kerfjs/ui/select/register';

import { Button } from '@kerfjs/ui/button';
import { ContentItem } from '@kerfjs/ui/content-item';
import { List } from '@kerfjs/ui/list';
import { Row } from '@kerfjs/ui/row';
import { Select } from '@kerfjs/ui/select';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import { type SafeHtml } from 'kerfjs';

import { isOrderableDecision, isOrderFulfilled, orderSummary, receiptSummary } from '../../../../shared/fulfilment.js';
import type { DecisionT, LineOrderT, LineSavingT } from '../../../data/api.js';
import { CurrencyAmount } from '../../currency-amount.js';
import { DecisionBadge } from '../../decision-badge.js';
import type { SummaryLine } from './summary-context.js';

/**
 * One decided line as a surface card: mono line number, title, decision badge, then either
 * the recorded purchase outcome (savings), a "no purchase needed" note for rejected lines, or
 * the controls to record the outcome. Presentational: the caller supplies the recorded
 * savings and the vendors that quoted the line.
 */
export function OutcomeRow({
  line,
  decision,
  savings,
  base,
  vendorOptions,
  preselectedVendor,
  locked = false,
  order,
}: {
  line: SummaryLine;
  decision: DecisionT;
  savings?: LineSavingT;
  base: string;
  vendorOptions: readonly { vendorId: string; vendorName: string }[];
  preselectedVendor: string;
  /** A closed or cancelled demand records no further outcomes (HS2-Y50H8E). */
  locked?: boolean;
  /** The line's current purchase order (FR-5.18, HS2-HYDRJX). */
  order?: LineOrderT;
}): SafeHtml {
  // Ordered sits between the decision and the outcome; once bought, the order line stays as a record.
  const canOrder = !locked && isOrderableDecision(decision.action);
  const orderLine =
    order !== undefined ? (
      <Row className="outcome-row-order" vAlign="middle" gap="xs" wrap>
        <Text variant="span" size="compact">
          {[orderSummary(order), receiptSummary(order)].filter((part) => part !== '').join(' · ')}
        </Text>
        {canOrder ? (
          <Button
            tone="neutral"
            emphasis="plain"
            size="compact"
            action="order-open"
            rootAttributes={{ 'data-line-id': line.id }}
          >
            Edit Order…
          </Button>
        ) : null}
        {/* Deliveries (FR-5.19, HS2-6AJZPE): record what arrived, or review and correct the receipts. */}
        {!locked ? (
          <Button
            tone="neutral"
            emphasis={isOrderFulfilled(order) ? 'plain' : 'secondary'}
            size="compact"
            action="receipt-open"
            rootAttributes={{ 'data-line-id': line.id }}
          >
            {isOrderFulfilled(order) ? 'Receipts…' : 'Record Receipt…'}
          </Button>
        ) : null}
      </Row>
    ) : canOrder && savings === undefined ? (
      <Row className="outcome-row-order" vAlign="middle" gap="xs" wrap>
        <Button
          tone="neutral"
          emphasis="secondary"
          size="compact"
          action="order-open"
          rootAttributes={{ 'data-line-id': line.id }}
        >
          Mark Ordered…
        </Button>
      </Row>
    ) : null;
  return (
    <ContentItem frame="framed" appearance="surface" rootAttributes={{ 'data-key': line.id }}>
      <div class="outcome-row">
        <Text variant="span" tone="quiet" size="compact" font="monospace" className="outcome-row-number">
          {String(line.lineNumber)}
        </Text>
        <Text variant="span">{line.description}</Text>
        <span class="outcome-row-decision">
          <DecisionBadge action={decision.action} />
        </span>
        <List gap="xs" className="outcome-row-fulfilment">
          {orderLine}
          {decision.action === 'reject' ? (
            <Text variant="span" tone="quiet" size="compact">
              No purchase outcome needed.
            </Text>
          ) : savings !== undefined ? (
            <List className="dr-outcome done">
              <Text variant="span">
                Bought at <CurrencyAmount value={savings.finalPrice} currency={base} /> · saved{' '}
                <CurrencyAmount value={savings.savingsVsQuote} currency={base} />
              </Text>
              {savings.savingsVsBenchmark !== null ? (
                <Text variant="span" tone="quiet" size="compact">
                  <CurrencyAmount value={savings.savingsVsBenchmark} currency={base} /> vs benchmark
                  {savings.benchmarkAnalysisVersion !== null
                    ? ` · pricing check v${String(savings.benchmarkAnalysisVersion)}`
                    : null}
                </Text>
              ) : null}
            </List>
          ) : locked ? (
            <Text variant="span" tone="quiet" size="compact">
              No outcome recorded.
            </Text>
          ) : (
            <Row className="dr-outcome" vAlign="bottom" gap="xs" wrap>
              <div class="dr-final">
                <TextField
                  size="compact"
                  type="number"
                  placeholder="final price"
                  name={`out_price_${line.id}`}
                  value={decision.approvedUnitPrice ?? decision.targetPrice ?? ''}
                  label={`Final Price (${base})`}
                />
              </div>
              {vendorOptions.length > 0 ? (
                <Select
                  className="dr-vendor"
                  size="compact"
                  name={`out_vendor_${line.id}`}
                  value={preselectedVendor}
                  label="Final Vendor"
                  choices={[
                    { value: '', label: 'Vendor (from Decision)' },
                    ...vendorOptions.map((vendor) => ({ value: vendor.vendorId, label: vendor.vendorName })),
                  ]}
                />
              ) : null}
              <Button
                tone="neutral"
                emphasis="primary"
                size="compact"
                rootAttributes={{ 'data-record-outcome': line.id }}
              >
                Record Outcome
              </Button>
            </Row>
          )}
        </List>
      </div>
    </ContentItem>
  );
}
